import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './ArticleDetail.css';

const API_URL = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : 'http://localhost:3001';

const ArticleDetail = () => {
  const { id, source } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [ideasData, setIdeasData] = useState(null); // State for ideas data
  const [quotes, setQuotes] = useState([]); // State to store quotes from ideas
  const [isPanelOpen, setIsPanelOpen] = useState(false); // State to control panel visibility
  const [selectedIdea, setSelectedIdea] = useState(null); // State to store the selected idea
  const [relatedArticles, setRelatedArticles] = useState([]); // New state for related articles
  const [loadingRelated, setLoadingRelated] = useState(false); // New state for loading status
  const [currentRelatedIndex, setCurrentRelatedIndex] = useState(0); // Track current related article
  const panelRef = useRef(null); // Add ref for the panel
  const [scrollPosition, setScrollPosition] = useState(0); // Store the initial scroll position when panel opens

  // Function to show the next related article
  const showNextRelatedArticle = () => {
    if (relatedArticles.length > 0) {
      setCurrentRelatedIndex(prevIndex => 
        prevIndex === relatedArticles.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Updated function to handle quote click with related articles and proper scroll handling
  const handleQuoteClick = async (ideaId, event) => {
    const idea = ideasData.find(idea => idea.id === ideaId);
    if (idea) {
      // Store current scroll position
      const currentScroll = window.scrollY;
      setScrollPosition(currentScroll);

      console.log(`Click at ${event.clientY}, Scroll at ${window.scrollY}`);

      setSelectedIdea(idea);
      setIsPanelOpen(true);

      // Focus the panel for accessibility
      setTimeout(() => {
        if (panelRef.current) {
          panelRef.current.focus();
        }
      }, 100);

      // Reset related articles to avoid showing stale data
      setRelatedArticles([]);
      
      // Check if the idea has an embedding
      if (idea.embedding) {
        try {
          setLoadingRelated(true);
          
          // Use the existing API endpoint to fetch similar articles based on the idea's embedding
          const response = await axios.post(`${API_URL}/api/similar-articles`, {
            embedding: idea.embedding
          });
          
          // Filter out the current article from results
          const filteredArticles = response.data.filter(article => article.json_id !== id);
          
          // Limit to 5 articles
          const topArticles = filteredArticles.slice(0, 5);
          setRelatedArticles(topArticles);
          
          console.log('Related articles:', topArticles);
        } catch (err) {
          console.error('Error fetching related articles:', err);
        } finally {
          setLoadingRelated(false);
        }
      } else {
        console.log('No embedding found for this idea');
      }
    }
  };

  // Updated function to close the panel
  const closePanel = () => {
    // Get the panel element
    const panelElement = document.querySelector('.idea-panel');
    if (panelElement) {
      // Add a closing class to ensure proper exit animation
      panelElement.classList.add('closing');
      // Use setTimeout to ensure the closing animation completes before state change
      setTimeout(() => {
        setIsPanelOpen(false);

        // Remove closing class after transition is complete
        setTimeout(() => {
          if (panelElement) panelElement.classList.remove('closing');
        }, 350); // Slightly longer than transition time
      }, 10);
    } else {
      setIsPanelOpen(false);
    }
  };

  // Clean up when unmounting - ensure body scroll is restored
  useEffect(() => {
    return () => {
      document.body.classList.remove('body-no-scroll');
    };
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // First, let's check if the file exists using our debug endpoint
        const checkPath = `${API_URL}/check-file/${source}/${id}`;
        console.log('Checking file availability at:', checkPath);
        const checkResult = await axios.get(checkPath);
        setDebugInfo(checkResult.data);
        
        console.log('File check result:', checkResult.data);
        
        // Determine the correct path based on the source
        const basePath = source === 'eflux' 
          ? `${API_URL}/data/eflux_json` 
          : `${API_URL}/data/kadist_json`;
        
        console.log('Attempting to fetch article from:', `${basePath}/${id}.json`);
        const response = await axios.get(`${basePath}/${id}.json`);
        setArticle(response.data);
        console.log('Article loaded successfully');

        // After successfully loading the article, fetch the ideas data
        fetchIdeasData();
      } catch (err) {
        console.error('Error loading article:', err);
        setError(`Failed to load article. Please try again later. (${err.message})`);
      } finally {
        setLoading(false);
      }
    };

    // Function to fetch ideas data
    const fetchIdeasData = async () => {
      try {
        // Determine the correct ideas path based on the source - update to use embeddings folder
        const ideasBasePath = source === 'eflux' 
          ? `${API_URL}/data/eflux_json_ideas_embeddings` 
          : `${API_URL}/data/kadist_json_ideas_embeddings`;
        
        console.log('Attempting to fetch ideas with embeddings from:', `${ideasBasePath}/${id}.json`);
        const ideasResponse = await axios.get(`${ideasBasePath}/${id}.json`);
        setIdeasData(ideasResponse.data);
        console.log('Ideas data with embeddings for article:', ideasResponse.data);
        
        // Extract quotes from ideas data (structure remains the same)
        if (ideasResponse.data && Array.isArray(ideasResponse.data)) {
          const extractedQuotes = ideasResponse.data
            .filter(idea => idea.quote)
            .map(idea => ({
              text: idea.quote
                .replace(/^"|"$/g, '') // Remove surrounding quotes
                .replace(/…/g, '...') // Normalize ellipses
                .replace(/\.$/, ''), // Remove trailing period
              originalQuote: idea.quote, // Keep original quote for display
              ideaId: idea.id,
              idea: idea.idea,
              tags: idea.tags,
              embedding: idea.embedding // Include embedding for related articles
            }));
          setQuotes(extractedQuotes);
          console.log('Extracted quotes from embeddings data:', extractedQuotes);
        }
      } catch (err) {
        console.log('No ideas data with embeddings found for this article:', err.message);
      }
    };

    fetchArticle();
  }, [id, source]);

  // Reset the related article index when new articles are loaded
  useEffect(() => {
    if (relatedArticles.length > 0) {
      setCurrentRelatedIndex(0);
    }
  }, [relatedArticles]);

  // Updated highlightQuotes function to highlight entire sentences
  function highlightQuotes(text, contextId = 'default') {
    if (!text || typeof text !== 'string' || quotes.length === 0) {
      return text;
    }

    const markers = [];
    const maxSentenceLength = 300; // Set a character limit for sentences

    quotes.forEach((quote, quoteIndex) => {
      const quoteText = quote.text;
      const startIndex = text.indexOf(quoteText);

      if (startIndex !== -1) {
        // Find the start of the sentence
        let sentenceStart = text.lastIndexOf('.', startIndex) + 1;
        if (sentenceStart === 0) {
          sentenceStart = 0; // No preceding period, start from the beginning
        }

        // Find the end of the sentence
        let sentenceEnd = text.indexOf('.', startIndex + quoteText.length);
        if (sentenceEnd === -1 || sentenceEnd - sentenceStart > maxSentenceLength) {
          sentenceEnd = Math.min(sentenceStart + maxSentenceLength, text.length);
        } else {
          sentenceEnd += 1; // Include the period
        }

        markers.push({
          start: sentenceStart,
          end: sentenceEnd,
          quote,
          quoteIndex,
        });
      }
    });

    // Sort markers by start position to ensure correct order
    markers.sort((a, b) => a.start - b.start);

    let result = [];
    let lastIndex = 0;

    markers.forEach((marker) => {
      if (marker.start > lastIndex) {
        result.push(text.substring(lastIndex, marker.start));
      }

      const uniqueKey = `${contextId}-q${marker.quote.ideaId}-i${marker.quoteIndex}-pos${marker.start}`;
      const sentenceContent = text.substring(marker.start, marker.end);

      result.push(
        <span 
          key={uniqueKey}
          className="highlighted-quote"
          data-idea-id={marker.quote.ideaId}
          onClick={(event) => handleQuoteClick(marker.quote.ideaId, event)}
          onMouseEnter={showIdeaBox} // Attach hover functionality
          onMouseLeave={hideIdeaBox} // Attach hover functionality
        >
          {sentenceContent}
          <div className="idea-hover-box" style={{ visibility: 'hidden', opacity: 0 }}>
            <p><strong>The idea behind this text:</strong> {marker.quote.idea || ''}</p>
            <button className="explore-related-button" onClick={() => handleExploreClick(marker.quote.ideaId)}>
              Explore Related Articles
            </button>
          </div>
        </span>
      );

      lastIndex = marker.end;
    });

    if (lastIndex < text.length) {
      result.push(text.substring(lastIndex));
    }

    return result.length === 1 ? result[0] : React.createElement(React.Fragment, null, ...result);
  }

  const showIdeaBox = (event) => {
    console.log('showIdeaBox triggered'); // Debugging log
    const quote = event.currentTarget;
    const ideaBox = quote.querySelector('.idea-hover-box');

    if (!ideaBox) {
      console.log('No idea-hover-box found'); // Debugging log
      return;
    }

    const quoteRect = quote.getBoundingClientRect();
    console.log('Quote rect:', quoteRect); // Debugging log

    ideaBox.style.top = `${quoteRect.top}px`;

    const articleWidth = document.querySelector('.article-main-content').getBoundingClientRect().width;
    const rightPosition = articleWidth + 20;

    ideaBox.style.left = `${rightPosition}px`;
    ideaBox.style.visibility = 'visible';
    ideaBox.style.opacity = '1';
    console.log('Popup shown at:', { top: ideaBox.style.top, left: ideaBox.style.left }); // Debugging log
  };

  const hideIdeaBox = (event) => {
    console.log('hideIdeaBox triggered'); // Debugging log
    const ideaBox = event.currentTarget.querySelector('.idea-hover-box');
    if (ideaBox) {
      ideaBox.style.visibility = 'hidden';
      ideaBox.style.opacity = '0';
      console.log('Popup hidden'); // Debugging log
    } else {
      console.log('No idea-hover-box found to hide'); // Debugging log
    }
  };

  const handleExploreClick = (ideaId) => {
    console.log(`Explore related articles for ideaId: ${ideaId}`);
    // Add logic to handle exploring related articles, e.g., navigating to a new page or opening a modal
  };

  const renderContent = (content, paragraphId) => {
    if (typeof content === 'string') {
      return highlightQuotes(content, `p${paragraphId || 'unknown'}`);
    } else if (typeof content === 'object') {
      if (content.text) {
        return (
          <span>
            {content.number ? `${content.number}. ` : ''}
            {highlightQuotes(content.text, `p${paragraphId || 'unknown'}-num${content.number || 'none'}`)}
          </span>
        );
      } else if (content.content) {
        return highlightQuotes(content.content, `p${paragraphId || 'unknown'}-content`);
      } else {
        return JSON.stringify(content);
      }
    }
    return content;
  };

  const renderParagraph = (paragraph) => {
    if (paragraph.type === 'media') {
      return (
        <div key={paragraph.id} className="article-media">
          {paragraph.media_url && (
            <img 
              src={paragraph.media_url} 
              alt={paragraph.caption || 'Article image'} 
            />
          )}
          {paragraph.caption && (
            <p className="caption">{paragraph.caption}</p>
          )}
        </div>
      );
    }
    
    return (
      <div key={paragraph.id} className={`paragraph-${paragraph.type}`}>
        {renderContent(paragraph.content, paragraph.id)}
      </div>
    );
  };

  if (loading) return <div className="article-loading">Loading article...</div>;
  
  if (error) {
    return (
      <div className="article-error">
        <h3>{error}</h3>
        {debugInfo && (
          <div className="debug-info">
            <h4>Debug Information:</h4>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            <p>Looking for file: {id}.json in {source} directory</p>
            <p>Server API URL: {API_URL}</p>
          </div>
        )}
        <Link to="/" className="back-link">← Back to search</Link>
      </div>
    );
  }
  
  if (!article) return <div className="article-error">Article not found</div>;

  return (
    <div className={`article-detail ${isPanelOpen ? 'panel-open' : ''}`}>
      <div className="article-main-content">
        <div className="article-header">
          <Link to="/" className="back-link" style={{ color: 'black' }}>← Back to search</Link>
          <h1>{article.title}</h1>
          {source === 'kadist' ? (
            <>
              {article.artist && <h2 className="artist-name">{article.artist}</h2>}
              {article.date && <h3 className="publication-date">{article.date}</h3>}
            </>
          ) : (
            article.author && <h2>By {article.author}</h2>
          )}
          {article.url && article.url.includes('/journal/') && (() => {
            const match = article.url.match(/\/journal\/(\d+)\//);
            return match ? <h3 className="issue-number">Issue #{match[1]}</h3> : null;
          })()}
        </div>

        {article.main_media && article.main_media.media_url && (
          <div className="article-media">
            <img 
              src={article.main_media.media_url} 
              alt={article.main_media.caption || article.title} 
            />
            {article.main_media.caption && (
              <p className="caption">{article.main_media.caption}</p>
            )}
          </div>
        )}

        <div className="article-body">
          {article.body && article.body.map(paragraph => renderParagraph(paragraph))}
        </div>

        {article.footnotes && article.footnotes.length > 0 && (
          <div className="article-footnotes">
            <h3>Footnotes</h3>
            {article.footnotes.map((footnote, index) => (
              <div key={index} className="footnote">
                {renderContent(footnote)}
              </div>
            ))}
          </div>
        )}

        {article.url && (
          <div className="article-source">
            <a href={article.url} target="_blank" rel="noopener noreferrer">
              View original article
            </a>
          </div>
        )}
      </div>

      <div 
        ref={panelRef}
        className={`idea-panel ${isPanelOpen ? 'open' : ''}`} 
        style={{ top: `${scrollPosition}px` }}
        tabIndex="-1"
      >
        <button className="close-panel-button" onClick={closePanel} aria-label="Close panel">×</button>
        {selectedIdea && (
          <div className="idea-panel-content">
            <div className="related-articles">
              <div className="related-header">
                <h4>THIS IDEA RELATES TO THE FOLLOWING ARTICLE</h4>
                {relatedArticles.length > 1 && (
                  <button 
                    className="reload-button" 
                    onClick={showNextRelatedArticle} 
                    title="Show next related article"
                  >
                    ↻
                  </button>
                )}
              </div>
              
              {loadingRelated ? (
                <p className="loading-text">Loading related articles...</p>
              ) : relatedArticles.length > 0 ? (
                <div className="single-related-article">
                  {(() => {
                    const article = relatedArticles[currentRelatedIndex];
                    return (
                      <div className="related-article-card">
                        <Link 
                          to={`/article/${article.json_id.startsWith('kadist') ? 'kadist' : 'eflux'}/${article.json_id}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/article/${article.json_id.startsWith('kadist') ? 'kadist' : 'eflux'}/${article.json_id}`, '_blank');
                            closePanel();
                          }}
                          className="related-article-link-full"
                        >
                          <h5 className="related-article-title" style={{ color: 'black' }}>{article.title}</h5>
                          
                          {article.media && (
                            <div className="related-article-media">
                              <img src={article.media} alt={article.title} />
                            </div>
                          )}
                          
                          <p className="related-article-summary">{article.summary}</p>
                          
                          <div className="related-article-footer">
                            <span className="article-source-badge">{article.source}</span>
                            <span className="article-counter">{currentRelatedIndex + 1}/{relatedArticles.length}</span>
                          </div>
                        </Link>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <p className="no-results">No related articles found</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleDetail;
